import { createFileRoute, Link } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { CarCard } from "@/components/CarCard";
import { Car, Shield, Handshake, ChevronRight, ArrowRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { CONTACT } from "@/config/contact";
import { buildSeoMeta } from "@/lib/seo";
import { buildAutoDealerJsonLd, buildWebSiteJsonLd } from "@/lib/jsonld";
import { useHeroSettings } from "@/hooks/use-hero-settings";
import { CityCoverageSection } from "@/components/CityCoverageSection";
import { HeroMediaBackground } from "@/components/HeroMediaBackground";
import santanderLogo from "@/assets/partners/santander.webp";
import fragusLogo from "@/assets/partners/fragus.webp";
import autobranchenLogo from "@/assets/partners/autobranchen.webp";

export const Route = createFileRoute("/")({
  head: () => {
    const seo = buildSeoMeta({
      title: "Bilforhandler i Børkop · AutoXChange ved Vejle & Kolding",
      description:
        "Uafhængig bilforhandler i Trekantområdet — 10 min fra Vejle, 25 min fra Kolding. Håndplukkede biler, fair priser, levering i hele Danmark.",
      path: "/",
      imageAlt: "AutoXChange — bilforhandler i Trekantområdet (Børkop, ved Vejle og Kolding)",
      geo: {
        region: "DK-83",
        placename: "Børkop",
        latitude: 55.6379,
        longitude: 9.6431,
      },
    });
    return {
      ...seo,
      scripts: [
        { type: "application/ld+json", children: buildAutoDealerJsonLd() },
        { type: "application/ld+json", children: buildWebSiteJsonLd() },
      ],
    };
  },
  component: Index,
});

function Index() {
  const { data: featuredCars } = useQuery({
    queryKey: ["featured-cars"],
    queryFn: async () => {
      const { data } = await supabase
        .from("cars")
        .select("id, brand, model, variant, year, mileage, price, fuel_type, transmission, color, body_type, doors, seats, engine_size, horsepower, description, equipment, images, status, is_new, is_popular, is_leasing, leasing_price, is_demo, first_registration, seo_title, seo_description, seo_slug, created_at")
        .eq("status", "active")
        .eq("is_demo", false)
        .order("created_at", { ascending: false })
        .limit(8);
      return data || [];
    },
  });

  const { data: heroSettings } = useHeroSettings("home");
  const heroType = heroSettings?.mediaType ?? "video";
  const heroUrl = heroSettings?.url ?? "";
  const isImageBg = heroType === "image" && heroUrl.trim().length > 0;

  return (
    <>
      {/* JSON-LD now emitted via head.scripts (AutoDealer + WebSite). */}
      {/* Hero — uses the shared HeroMediaBackground so iframe / video / image
          behaviour (autoplay, mute, loop, playsinline, referrer-policy,
          diagnostics, debug ?heroAutoplay=0 toggle) stays identical to every
          other hero on the site. The home hero keeps its custom centered
          content (badge, h1, paragraph, CTA) on top of the shared background. */}
      <section
        className="relative flex min-h-[90vh] items-end overflow-hidden bg-dark-blue bg-cover bg-center pb-12 md:items-center md:pb-0"
        style={isImageBg ? { backgroundImage: `url(${heroUrl})` } : undefined}
      >
        <HeroMediaBackground
          source="home"
          mediaType={heroType}
          url={heroUrl}
          mediaClassName="opacity-60"
        />
        {/* Gradient overlay — on mobile fade from bottom so the video stays visible
            at the top; on desktop keep the left-to-right fade for readable text. */}
        <div className="absolute inset-0 bg-gradient-to-t from-dark-blue via-dark-blue/80 to-dark-blue/20 md:bg-gradient-to-r md:from-dark-blue md:via-dark-blue/90 md:to-dark-blue/50" />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-brand-badge-bg px-3 py-1 text-xs font-medium text-brand-badge-text sm:px-4 sm:py-1.5 sm:text-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-badge-dot" />
              <span className="sm:hidden">Bilforhandler i Trekantområdet</span>
              <span className="hidden sm:inline">Bilforhandler i Trekantområdet — Børkop ved Vejle & Kolding</span>
            </div>
            <h1 className="mt-5 text-4xl font-bold leading-[1.1] tracking-tight text-white sm:mt-6 sm:text-5xl md:text-6xl lg:text-7xl">
              Din lokale bilforhandler i{" "}
              <span className="text-accent">Trekantområdet</span>
            </h1>
            <p className="mt-4 max-w-lg text-base leading-relaxed text-white/80 sm:mt-6 sm:text-lg">
              AutoXChange ligger centralt i Børkop — 10 minutter fra Vejle, 25 fra Kolding og tæt på Fredericia. Vi håndplukker hver bil, sætter fair priser og leverer i hele Danmark.
            </p>
            <div className="mt-8 flex flex-wrap gap-4 sm:mt-10">
              <Link
                to="/biler-til-salg"
                className="inline-flex items-center gap-2 rounded-lg bg-accent px-6 py-3 text-sm font-semibold text-accent-foreground transition-all hover:brightness-110 sm:px-8 sm:py-4"
              >
                Se aktuelle biler <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Cars — render a fixed-height section so the layout does not
          shift once the query resolves. Skeleton cards match the real CarCard
          aspect ratio (image 16:9 + ~120px of text/footer) to keep CLS stable. */}
      <section className="bg-muted py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="flex items-end justify-between">
            <div>
              <span className="text-sm font-semibold uppercase tracking-widest text-accent">Netop landet</span>
              <h2 className="mt-3 text-3xl font-bold tracking-tight text-foreground">Seneste biler på pladsen</h2>
            </div>
            <Link to="/biler-til-salg" className="hidden items-center gap-2 rounded-lg bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-all hover:brightness-125 md:inline-flex">
              Se hele udvalget <ChevronRight size={14} />
            </Link>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {featuredCars && featuredCars.length > 0
              ? featuredCars.map((car) => <CarCard key={car.id} {...car} />)
              : Array.from({ length: 8 }).map((_, i) => (
                  <div
                    key={`car-skeleton-${i}`}
                    aria-hidden="true"
                    className="overflow-hidden rounded-xl bg-card ring-1 ring-border"
                  >
                    <div className="aspect-[16/10] w-full bg-muted-foreground/10" />
                    <div className="space-y-3 p-4">
                      <div className="h-4 w-3/4 rounded bg-muted-foreground/10" />
                      <div className="h-3 w-1/2 rounded bg-muted-foreground/10" />
                      <div className="h-5 w-1/3 rounded bg-muted-foreground/10" />
                    </div>
                  </div>
                ))}
          </div>
          <div className="mt-12 flex justify-center">
            <Link
              to="/biler-til-salg"
              className="inline-flex items-center gap-2 rounded-lg bg-accent px-8 py-4 text-sm font-semibold text-accent-foreground transition-all hover:brightness-110"
            >
              Se alle biler <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* USPs */}
      <section className="bg-background py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center">
            <span className="text-sm font-semibold uppercase tracking-widest text-accent">Det vi står for</span>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-foreground md:text-4xl">Bilhandel på dine vilkår</h2>
          </div>

          <div className="mt-16 grid gap-8 md:grid-cols-3">
            {[
              { icon: Car, title: "Håndplukkede biler", text: "Vi tager ikke alt ind. Hver bil bliver nøje udvalgt, testet og klargjort, før den når ud til dig. EU-import – ingen import udenfor EU — kun biler med dokumenteret historik." },
              { icon: Handshake, title: "Gennemskuelig pris", text: "Den pris du ser, er den pris du betaler. Ingen leveringsgebyr, dokumentationsgebyr eller andre kreative tillæg. Hvad du ser er hvad du får." },
              { icon: Shield, title: "Ingen overraskelser", text: "Vi oplyser alt — også det der ikke er perfekt. Fuld servicehistorik, eventuelle skader og realistisk stand. Du ved præcis hvad du køber." },
            ].map((item) => (
              <div key={item.title} className="group rounded-2xl border border-border bg-card p-8 transition-all hover:shadow-lg hover:border-accent/20">
                <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-accent/10 text-accent transition-colors group-hover:bg-accent group-hover:text-accent-foreground">
                  <item.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-6 text-xl font-bold text-foreground">{item.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-dark-blue py-24">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1280&h=400&fit=crop&auto=format&q=60&fm=webp')] bg-cover bg-center opacity-10" />
        <div className="relative z-10 mx-auto max-w-3xl px-6 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white md:text-4xl">Skal vi finde den rigtige til dig?</h2>
          <p className="mt-4 text-lg text-white/80">
            Fortæl os hvad du leder efter — mærke, budget, behov — så finder vi den. Eller kig forbi pladsen i Børkop.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/biler-til-salg" className="rounded-lg bg-accent px-8 py-4 text-sm font-semibold text-accent-foreground transition-all hover:brightness-110">
              Se aktuelle biler
            </Link>
            <Link to="/kontakt" className="rounded-lg border border-white/20 px-8 py-4 text-sm font-semibold text-white transition-all hover:bg-white/10">
              Skriv eller ring
            </Link>
          </div>
        </div>
      </section>

      {/* Partners / trust logos */}
      <section className="bg-background py-16 border-t border-border">
        <div className="mx-auto max-w-7xl px-6">
          <p className="text-center text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Samarbejdspartnere
          </p>
          <div className="mt-10 grid grid-cols-1 items-center justify-items-center gap-4 sm:grid-cols-3 sm:gap-6">
            {[
              {
                src: santanderLogo,
                alt: "Santander Consumer Bank — finansieringspartner",
                href: "https://www.santanderconsumer.dk",
                label: "Besøg Santander Consumer Bank (åbner i ny fane)",
              },
              {
                src: fragusLogo,
                alt: "Fragus — garantipartner",
                href: "https://www.fragus.dk",
                label: "Besøg Fragus (åbner i ny fane)",
              },
              {
                src: autobranchenLogo,
                alt: "AutoBranchen Danmark — brancheforening",
                href: "https://www.autobranchen.dk",
                label: "Besøg AutoBranchen Danmark (åbner i ny fane)",
              },
            ].map((logo) => (
              <a
                key={logo.alt}
                href={logo.href}
                target="_blank"
                rel="noopener noreferrer nofollow"
                aria-label={logo.label}
                className="flex h-24 w-full max-w-[280px] items-center justify-center rounded-lg bg-white px-6 py-3 ring-1 ring-border/60 shadow-sm transition hover:shadow-md hover:ring-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:ring-white/15 md:h-28"
              >
                <img
                  src={logo.src}
                  alt={logo.alt}
                  loading="lazy"
                  decoding="async"
                  width={220}
                  height={64}
                  className="block h-14 w-auto max-w-full object-contain md:h-16"
                />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* SEO city coverage — directly above footer */}
      <CityCoverageSection />
    </>
  );
}
